import { createSignal, For, Show, type Accessor, type JSX } from "solid-js";

import { Button } from "~/common/button";
import clsx from "~/util/html/style";

import {
  MARK_COLOR_ORDER,
  MARK_COLORS,
  MARK_WIDTH,
  newStrokeId,
  strokePath,
  strokesNear,
  type MarkColor,
  type Markup,
  type MarkTool,
  type Stroke,
} from "./markup";
import { STAGE_HEIGHT, STAGE_WIDTH } from "./stage";

type PenTool = MarkTool | "eraser";

// How far from the finger the eraser reaches, in stage units, past the edge of a line. A fingertip is wide.
const ERASER_REACH = 14;
// Points closer than this to the last one add nothing a patient could see.
const MIN_STEP = 1.5;

/** The drawing tools on one viewer: whether drawing is on, the tool, and a color kept for each tool, so the pen stays
 *  red while the highlighter stays yellow. */
export type Pen = {
  on: Accessor<boolean>;
  setOn: (on: boolean) => void;
  tool: Accessor<PenTool>;
  setTool: (tool: PenTool) => void;
  color: Accessor<MarkColor>;
  setColor: (color: MarkColor) => void;
};

export function createPen(): Pen {
  const [on, setOn] = createSignal(false);
  const [tool, setTool] = createSignal<PenTool>("pen");
  const [colors, setColors] = createSignal<Record<MarkTool, MarkColor>>({
    pen: "red",
    highlighter: "yellow",
  });
  const inkTool = (): MarkTool => {
    const current = tool();
    return current === "eraser" ? "pen" : current;
  };
  return {
    on,
    setOn,
    tool,
    setTool,
    color: () => colors()[inkTool()],
    setColor: (color) => {
      // A color picked with the eraser in hand means "draw in this color".
      const ink = inkTool();
      if (tool() === "eraser") setTool("pen");
      setColors((all) => ({ ...all, [ink]: color }));
    },
  };
}

/** The strokes on a slide, drawn over it at stage size. */
export function MarkupStrokes(props: { strokes: Stroke[] }): JSX.Element {
  return (
    <For each={props.strokes}>
      {(stroke) => (
        <path
          d={strokePath(stroke.points)}
          fill="none"
          stroke={MARK_COLORS[stroke.color].hex}
          stroke-width={MARK_WIDTH[stroke.tool]}
          stroke-opacity={stroke.tool === "highlighter" ? 0.5 : 1}
          stroke-linecap="round"
          stroke-linejoin="round"
          data-stroke={stroke.id}
          data-tool={stroke.tool}
        />
      )}
    </For>
  );
}

/** The sheet over the slide on the stage. With drawing on it takes the pointer, mouse, finger or stylus, and draws or
 *  erases; with drawing off it lets every click through to the slide. Only the first finger draws, so a second one
 *  resting on the glass leaves no line. */
export function MarkupLayer(props: { markup: Markup; slide: string; pen: Pen }): JSX.Element {
  let svg!: SVGSVGElement;
  let drawing: { pointer: number; slide: string; stroke: Stroke } | null = null;
  // Where the eraser last was, so each move sweeps the stretch since.
  let erasing: { pointer: number; slide: string; x: number; y: number } | null = null;

  const toStage = (event: PointerEvent): [number, number] => {
    const box = svg.getBoundingClientRect();
    return [
      ((event.clientX - box.left) * STAGE_WIDTH) / (box.width || STAGE_WIDTH),
      ((event.clientY - box.top) * STAGE_HEIGHT) / (box.height || STAGE_HEIGHT),
    ];
  };

  const erase = (event: PointerEvent, gesture: "start" | "more") => {
    if (!erasing) return;
    const [x, y] = toStage(event);
    const { slide } = erasing;
    props.markup.erase(
      slide,
      strokesNear(props.markup.strokes(slide), x, y, ERASER_REACH, erasing.x, erasing.y),
      gesture,
    );
    erasing.x = x;
    erasing.y = y;
  };

  const onPointerDown = (event: PointerEvent) => {
    if (!props.pen.on() || !event.isPrimary) return;
    if (event.pointerType === "mouse" && event.button !== 0) return;
    // preventDefault keeps the page from taking the touch, and with it the focus: the stage keeps the arrow keys.
    event.preventDefault();
    svg.closest<HTMLElement>("[tabindex]")?.focus({ preventScroll: true });
    if ("setPointerCapture" in svg) svg.setPointerCapture(event.pointerId);
    const slide = props.slide;
    if (props.pen.tool() === "eraser") {
      const [x, y] = toStage(event);
      erasing = { pointer: event.pointerId, slide, x, y };
      erase(event, "start");
      return;
    }
    const tool = props.pen.tool() === "highlighter" ? "highlighter" : "pen";
    const stroke: Stroke = {
      id: newStrokeId(),
      tool,
      color: props.pen.color(),
      points: toStage(event),
    };
    drawing = { pointer: event.pointerId, slide, stroke };
    props.markup.draw(slide, stroke);
  };

  const onPointerMove = (event: PointerEvent) => {
    if (erasing?.pointer === event.pointerId) {
      const events = "getCoalescedEvents" in event ? event.getCoalescedEvents() : [];
      for (const each of events.length > 0 ? events : [event]) erase(each, "more");
      return;
    }
    if (drawing?.pointer !== event.pointerId) return;
    // A fast line between frames comes as several events in one; the browsers without the list send each alone.
    const events = "getCoalescedEvents" in event ? event.getCoalescedEvents() : [];
    const points = [...drawing.stroke.points];
    for (const each of events.length > 0 ? events : [event]) {
      const [x, y] = toStage(each);
      const lastX = points.at(-2) ?? x;
      const lastY = points.at(-1) ?? y;
      if (Math.hypot(x - lastX, y - lastY) >= MIN_STEP) points.push(x, y);
    }
    if (points.length === drawing.stroke.points.length) return;
    drawing.stroke = { ...drawing.stroke, points };
    props.markup.draw(drawing.slide, drawing.stroke);
  };

  const onPointerEnd = (event: PointerEvent) => {
    if (erasing?.pointer === event.pointerId) erasing = null;
    if (drawing?.pointer !== event.pointerId) return;
    props.markup.add(drawing.slide, drawing.stroke);
    drawing = null;
  };

  return (
    <svg
      ref={svg}
      class={clsx(
        // Over the slide's own raised drawings (z-index 1), by coming after them.
        "absolute inset-0 z-base size-full",
        props.pen.on() ? "cursor-crosshair touch-none" : "pointer-events-none",
      )}
      viewBox={`0 0 ${STAGE_WIDTH} ${STAGE_HEIGHT}`}
      aria-hidden="true"
      data-markup-layer
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerEnd}
      onPointerCancel={onPointerEnd}
    >
      <MarkupStrokes strokes={props.markup.strokes(props.slide)} />
      <MarkupStrokes strokes={props.markup.drawing(props.slide)} />
    </svg>
  );
}

/** The toolbar's words, for a screen in the patient's language (the office tablet). Staff screens use the English. */
export type MarkupLabels = {
  draw: string;
  tools: string;
  pen: string;
  highlighter: string;
  eraser: string;
  color: string;
  colors: Record<MarkColor, string>;
  undo: string;
  clear: string;
  done: string;
};

export const ENGLISH_MARKUP_LABELS: MarkupLabels = {
  draw: "Draw",
  tools: "Drawing tools",
  pen: "Pen",
  highlighter: "Highlighter",
  eraser: "Eraser",
  color: "Color",
  colors: {
    red: MARK_COLORS.red.label,
    yellow: MARK_COLORS.yellow.label,
    green: MARK_COLORS.green.label,
    blue: MARK_COLORS.blue.label,
  },
  undo: "Undo",
  clear: "Clear slide",
  done: "Done",
};

const TOOLS: PenTool[] = ["pen", "highlighter", "eraser"];

/** Draw beside the deck's controls, and once drawing is on the tools: pen, highlighter, eraser, the colors, Undo and
 *  Clear for the slide on the stage, and Done. */
export function MarkupToolbar(props: {
  markup: Markup;
  slide: string | undefined;
  pen: Pen;
  dark?: boolean;
  labels?: MarkupLabels;
}): JSX.Element {
  const plain = (): "plain" | "secondary" => (props.dark ? "plain" : "secondary");
  const words = () => props.labels ?? ENGLISH_MARKUP_LABELS;
  return (
    <Show
      when={props.pen.on()}
      fallback={
        <Button
          variant={plain()}
          dark={props.dark}
          size="small"
          leftIconName="pencil"
          onClick={() => props.pen.setOn(true)}
        >
          {words().draw}
        </Button>
      }
    >
      <div
        class="flex flex-wrap items-center justify-center gap-2"
        role="toolbar"
        aria-label={words().tools}
      >
        <For each={TOOLS}>
          {(tool) => (
            <Button
              variant={props.pen.tool() === tool ? "primary" : plain()}
              dark={props.dark}
              size="small"
              aria-pressed={props.pen.tool() === tool}
              onClick={() => props.pen.setTool(tool)}
            >
              {words()[tool]}
            </Button>
          )}
        </For>
        <div class="flex items-center gap-1.5 px-1" role="group" aria-label={words().color}>
          <For each={MARK_COLOR_ORDER}>
            {(color) => (
              <button
                type="button"
                class={clsx(
                  "size-6 rounded-full ring-offset-2 transition-shadow",
                  MARK_COLORS[color].swatch,
                  props.dark ? "ring-offset-[#08171D]" : "ring-offset-white",
                  props.pen.tool() !== "eraser" && props.pen.color() === color
                    ? clsx("ring-2", props.dark ? "ring-white" : "ring-brand-primary")
                    : "ring-1 ring-black/20",
                )}
                aria-label={words().colors[color]}
                aria-pressed={props.pen.tool() !== "eraser" && props.pen.color() === color}
                onClick={() => props.pen.setColor(color)}
              />
            )}
          </For>
        </div>
        <Button
          variant={plain()}
          dark={props.dark}
          size="small"
          leftIconName="arrow-uturn-left"
          disabled={!props.slide || !props.markup.canUndo(props.slide)}
          onClick={() => props.slide && props.markup.undo(props.slide)}
        >
          {words().undo}
        </Button>
        <Button
          variant={plain()}
          dark={props.dark}
          size="small"
          leftIconName="trash"
          disabled={!props.slide || props.markup.strokes(props.slide).length === 0}
          onClick={() => props.slide && props.markup.clear(props.slide)}
        >
          {words().clear}
        </Button>
        <Button
          variant={plain()}
          dark={props.dark}
          size="small"
          leftIconName="check"
          onClick={() => props.pen.setOn(false)}
        >
          {words().done}
        </Button>
      </div>
    </Show>
  );
}
